import { buildDeviceMappingIdSet as buildMappingIdSetFromRows } from '../../../helpers/deviceMappingRows';
import { getDeviceValidationMismatchReasons } from '../../../helpers/deviceValidation';
import type {
  RearDevicePackagingValidation,
  RearDevicePackagingValidationInput,
} from '../types/EventsOverviewTypes';

export type { RearDevicePackagingValidation, RearDevicePackagingValidationInput };

/**
 * Rear fleet ID format used in device-package-assignments.csv. Fleet IDs can include mixed
 * alphanumerics after the prefix (e.g. Rear_3P79G), not just digits-then-one-letter.
 */
export const REAR_DEVICE_ID_RE = /^Rear_[A-Za-z0-9]+$/i;

export function buildDeviceMappingIdSet(
  devices: Record<string, string>[],
): Set<string> {
  return buildMappingIdSetFromRows(devices);
}

/** Master list + rear ID format (sync). */
export function validateRearDeviceMappingAndFormat(
  deviceId: string,
  masterIds: Set<string> | null,
): RearDevicePackagingValidation {
  const did = deviceId.trim();
  if (!did) {
    return { ok: false, reason: 'Rear device ID is required' };
  }
  if (!REAR_DEVICE_ID_RE.test(did)) {
    return { ok: false, reason: 'Rear device ID must look like Rear_1234A' };
  }
  if (masterIds === null) {
    return { ok: false, reason: 'Could not load device mappings' };
  }
  if (masterIds.size > 0 && !masterIds.has(did)) {
    return { ok: false, reason: 'Rear device ID not in device mappings' };
  }
  return { ok: true };
}

/**
 * Master list + race configuration vs live device status and lifecycle
 * (same rules as assigned-devices validation / validation report).
 */
export function validateRearDeviceForPackaging(
  input: RearDevicePackagingValidationInput,
): RearDevicePackagingValidation {
  const master = validateRearDeviceMappingAndFormat(input.deviceId, input.masterIds);
  if (!master.ok) return master;

  if (!input.raceConfigLoadOk) {
    return { ok: false, reason: 'Could not load race configuration' };
  }

  const reasons = getDeviceValidationMismatchReasons(
    input.raceConfig,
    input.status,
    input.lifecycleRow,
  );
  if (reasons.length > 0) {
    return { ok: false, reason: reasons.join('; ') };
  }

  return { ok: true };
}
